import { Component, DestroyRef, inject, input, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NomenclatureType, TypeDiplome } from '@app/core/models/nomenclature.model';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { AbstractDescripteursComponent } from '@app/shared/form-models/abstract-descripteurs.component';
import { NomenclatureService, Operateur, Predicats } from '@app/shared/nomenclature/nomenclature.service';
import { BacASable, Espace, Periode, TypeObjetMaquette } from '@pcscol/odf-api-v1-angular';
import { combineLatest, Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { DescripteursEnqueteForm } from './descripteurs-enquete-form';
import { Traitement } from '@app/shared/autorisations/traitements';
import { IfIsNotAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { MatCardContent, MatCardTitle } from '@angular/material/card';
import { SelectNomenclatureComponent } from '@app/shared/select-nomenclature/select-nomenclature/select-nomenclature.component';
import { MatError, MatFormField, MatHint, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { FormMatErrorsDirective } from '@app/shared/base-form/form-mat-errors/form-mat-errors.directive';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatCheckbox } from '@angular/material/checkbox';
import { AsyncPipe } from '@angular/common';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { LANGUES_SISE, LangueSise } from './langues-sise';

@Component({
    selector: 'app-formulaire-descripteurs-enquete',
    templateUrl: './descripteurs-enquete.component.html',
    styleUrls: ['./descripteurs-enquete.component.scss'],
    imports: [
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        SelectNomenclatureComponent,
        MatFormField,
        MatLabel,
        MatInput,
        MatHint,
        MatSuffix,
        FormMatErrorsDirective,
        MatError,
        MatIconButton,
        MatTooltip,
        MatIcon,
        MatDivider,
        MatCheckbox,
        MatButton,
        AsyncPipe,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatOption
    ]
})
export class DescripteursEnqueteComponent extends AbstractDescripteursComponent implements OnInit {
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly commandFactoryService = inject(CommandFactoryService);
    private readonly notificationService = inject(NotificationService);
    private readonly nomenclaturesService = inject(NomenclatureService);
    private readonly destroyRef = inject(DestroyRef);
    readonly espace = input.required<Espace>();
    readonly objetMaquette = input<ObjetMaquette>();
    readonly typeObjetMaquette = input<TypeObjetMaquette>();
    readonly NomenclatureType = NomenclatureType;
    form: DescripteursEnqueteForm;
    @Input() objetMaquetteSelected: ObjetMaquette;
    readonly isPIAInActualContext = input.required<boolean>();
    readonly languesSiseFiltrees = signal<LangueSise[]>(LANGUES_SISE);

    constructor() {
        super();
        this.typesDiplomes$ = this.nomenclaturesService.listTypesDiplome().pipe(takeUntilDestroyed());
    }
    typesDiplomes$: Observable<TypeDiplome[]>;
    peutAjouterDomaineFormation$: Observable<boolean>;

    ngOnInit() {
        this.form = DescripteursEnqueteForm.createForm(
            this.fb,
            this.typeObjetMaquette()!,
            this.typesDiplomes$,
            this.objetMaquetteSelected.descripteursEnquete
        );
        const domainesFormation$ = this.nomenclaturesService.listNomenclatures(
            NomenclatureType.DomaineFormation,
            undefined,
            undefined,
            this.predicatsNomenclaturesValides
        );
        this.peutAjouterDomaineFormation$ = combineLatest([domainesFormation$, this.form.domainesFormationSelectionnes$]).pipe(
            map(([domaines, domainesSelectionnes]) => domaines.some(domaine => !domainesSelectionnes.includes(domaine.code)))
        );
        // if (!this.isPIAInActualContext) {
        //     this.form.disable();
        // }
        this.disableFormIfNotAutorised();
        this.form.codeLangueSiseControl.valueChanges
            .pipe(
                startWith(this.form.codeLangueSiseControl.value),
                map(value =>
                    LANGUES_SISE.filter(
                        langueSise =>
                            !value ||
                            langueSise.code.toLowerCase().includes(value.toLowerCase()) ||
                            langueSise.libelle.toLowerCase().includes(value.toLowerCase())
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(v => this.languesSiseFiltrees.set(v));
    }

    @IfIsNotAuthorized(Traitement.ODF_MODIFIER_DESCRIPTEURS_ENQUETE_OBJET_MAQUETTE)
    disableFormIfNotAutorised() {
        this.form.disable();
    }

    modifierDescripteursEnquete() {
        this.commandFactoryService
            .modifierDescripteursEnqueteObjetMaquetteCommand(
                this.objetMaquetteSelected.id!,
                this.objetMaquetteSelected.version,
                this.objetMaquetteSelected.descripteursEnquete!,
                this.form.asDescripteursEnquete
            )
            .do()
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe(om => {
                this.objetMaquetteSelected = om;
                this.form.asFormGroup.markAsPristine();
                this.notificationService.success(`Les descripteurs pour enquêtes ont été enregistrés avec succès`);
            });
    }

    getCodeEtLibelleLong(n) {
        return n.code + ' ' + n.libelleLong;
    }

    affichageLangueSise = (code: string | null): string => {
        if (!code) return '';
        const langue = LANGUES_SISE.find(langueSise => langueSise.code === code);
        return langue ? `${langue.code} - ${langue.libelle}` : code;
    };

    resetCodeLangueSise() {
        this.form.codeLangueSiseControl.reset();
        this.form.asFormGroup.markAsDirty();
    }

    private get dateConsommationNomenclatures(): string {
        const espace = this.espace();
        return (espace as BacASable).dateConsommationNomenclatures ?? (espace as Periode).validite.dateDebut;
    }

    private get predicatsNomenclaturesValides(): Predicats {
        return new Predicats()
            .add('dateDebutValidite', Operateur.LessOrEqual, this.dateConsommationNomenclatures)
            .add('dateFinValidite', Operateur.GreaterOrEqual, this.dateConsommationNomenclatures)
            .visible();
    }
}
